package com.siriusxm.pia.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span

/**
 * Data class representing a single SVG path element and its rendering properties.
 * Multiple SVGPath instances can be combined in an SVGIcon for multi-color icons.
 *
 * @param d The SVG path data (d attribute)
 * @param fill If true, renders the path using fill instead of stroke (default: false)
 * @param color The color for this path. Null means "currentColor" (inherits from CSS).
 *              Use explicit colors like "#E01E5A" for brand icons.
 * @param strokeLinecap The stroke-linecap value, only used when fill=false (default: "round")
 * @param strokeLinejoin The stroke-linejoin value, only used when fill=false (default: "round")
 */
data class SVGPath(
    val d: String,
    val fill: Boolean = false,
    val color: String? = null,
    val strokeLinecap: String = "round",
    val strokeLinejoin: String = "round"
)

/**
 * Data class representing a complete SVG icon, which may contain one or more paths.
 * This allows for multi-color icons (like brand logos) while maintaining simplicity
 * for single-path icons.
 *
 * @param paths The list of SVGPath elements that make up this icon
 * @param viewBox The SVG viewBox attribute (default: "0 0 24 24")
 */
data class SVGIcon(
    val paths: List<SVGPath>,
    val viewBox: String = "0 0 24 24"
)

/**
 * Creates an SVGIcon from a single path string. Use for simple stroke-based icons.
 */
fun SVGIcon(d: String): SVGIcon = SVGIcon(
    paths = listOf(SVGPath(d = d)),
    viewBox = "0 0 24 24"
)

/**
 * Creates an SVGIcon from a single SVGPath with a custom viewBox.
 */
fun SVGIcon(path: SVGPath, viewBox: String = "0 0 24 24"): SVGIcon = SVGIcon(
    paths = listOf(path),
    viewBox = viewBox
)

/**
 * Configuration for rendering an SVG icon.
 * Supports sizing, custom styling, tooltips, and click actions.
 */
class SvgIconConfiguration {
    /** The size of the icon (width and height). Defaults to 24px. */
    var size: CSSNumeric = 24.px

    /** Optional tooltip text displayed on hover. */
    var title: String? = null

    /** Custom cursor style. If null and action is set, defaults to "pointer". */
    var cursor: String? = null

    internal var styles: StyleScope.() -> Unit = {}
    internal var action: (suspend () -> Unit)? = null

    /**
     * Apply custom CSS styles to the icon container.
     */
    fun style(builder: StyleScope.() -> Unit) {
        styles = builder
    }

    /**
     * Set a click action for the icon. When set, the icon becomes clickable
     * and shows a pointer cursor by default.
     */
    fun action(block: suspend () -> Unit) {
        this.action = block
    }
}

/**
 * Renders an SVG icon as an inline SVG element.
 *
 * This composable renders SVG path data as an inline SVG, supporting both single-color
 * icons (that inherit from CSS via currentColor) and multi-color icons (with explicit
 * colors per path). Icons are sourced from the SVGIcons object.
 *
 * @param icon The SVGIcon containing one or more paths and viewBox configuration
 * @param init Configuration block for customizing the icon's size, style, and behavior
 *
 * Example usage:
 * ```kotlin
 * // Basic usage
 * svgIcon(SVGIcons.chevronRight)
 *
 * // With configuration
 * svgIcon(SVGIcons.xMark) {
 *     size = 20.px
 *     title = "Close"
 *     action { onClose() }
 * }
 *
 * // Multi-color brand icon
 * svgIcon(SVGIcons.slackColor) {
 *     size = 32.px
 * }
 * ```
 */
@Composable
fun svgIcon(icon: SVGIcon, init: SvgIconConfiguration.() -> Unit = {}) {
    val config = SvgIconConfiguration().apply(init)
    val coroutineScope = rememberCoroutineScope()

    Span({
        config.title?.let { title(it) }

        style {
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)

            if (config.cursor != null) {
                cursor(config.cursor!!)
            } else if (config.action != null) {
                cursor("pointer")
            }

            config.styles(this)
        }

        if (config.action != null) {
            onClick {
                it.stopPropagation()
                it.preventDefault()
                coroutineScope.launch {
                    config.action?.invoke()
                }
            }
        }
    }) {
        DisposableEffect(icon, config.size) {
            val pathsHtml = icon.paths.joinToString("\n") { path ->
                if (path.fill) {
                    val fillColor = path.color ?: "currentColor"
                    """<path fill="$fillColor" d="${path.d}"/>"""
                } else {
                    val strokeColor = path.color ?: "currentColor"
                    """<path fill="none" stroke="$strokeColor" stroke-width="1.5" stroke-linecap="${path.strokeLinecap}" stroke-linejoin="${path.strokeLinejoin}" d="${path.d}"/>"""
                }
            }

            val svg = """
                <svg xmlns="http://www.w3.org/2000/svg"
                     viewBox="${icon.viewBox}"
                     width="${config.size}"
                     height="${config.size}">
                    $pathsHtml
                </svg>
            """.trimIndent()

            scopeElement.innerHTML = svg
            onDispose { }
        }
    }
}

/**
 * Registry of SVG icons.
 *
 * New icons should be sourced from Tabler Icons (https://tabler.io/icons).
 * To add a new icon, fetch the SVG from GitHub and extract the path's `d` attribute:
 *
 * URL pattern: https://raw.githubusercontent.com/tabler/tabler-icons/main/icons/outline/{icon-name}.svg
 *
 * Icon names use camelCase here (e.g., chevronRight) but are kebab-case in the
 * Tabler repository (e.g., chevron-right).
 *
 * Note: Some existing icons below were sourced from Heroicons and remain for backward compatibility.
 */
object SVGIcons {
    // Basic UI icons (stroke-based, from Heroicons)
    val chevronRight = SVGIcon("M8.25 4.5L15.75 12L8.25 19.5")
    val chevronLeft = SVGIcon("M15.75 19.5L8.25 12L15.75 4.5")
    // Tabler "tool" (wrench) — used to mark a tool call in the chat transcript admin view (UCA-1091).
    val tool = SVGIcon("M7 10h3v-3l-3.5 -3.5a6 6 0 0 1 8 8l6 6a2 2 0 0 1 -3 3l-6 -6a6 6 0 0 1 -8 -8l3.5 3.5")
    // Tabler outline alert-triangle.
    val alertTriangle = SVGIcon("M12 9v4 M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0 M12 16h.01")
    val xMark = SVGIcon("M6 18L18 6M6 6L18 18")
    val check = SVGIcon("M4.5 12.75L10.5 18.75L19.5 5.25")
    val plus = SVGIcon("M12 4.5V19.5M19.5 12L4.5 12")
    val minus = SVGIcon("M5 12H19")

    // Media icons (stroke-based, from Tabler Icons)
    val playerPlay = SVGIcon("M7 4v16l13 -8l-13 -8")

    // Filled play/pause for the audio player's prominent transport button (from Tabler Icons,
    // filled variants) — solid glyphs read better inside the circular play/pause control.
    val playerPlayFilled = SVGIcon(
        SVGPath(d = "M6 4v16a1 1 0 0 0 1.524 .852l13 -8a1 1 0 0 0 0 -1.704l-13 -8a1 1 0 0 0 -1.524 .852z", fill = true)
    )
    val playerPauseFilled = SVGIcon(
        paths = listOf(
            SVGPath(d = "M9 4h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h2a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2z", fill = true),
            SVGPath(d = "M17 4h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h2a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2z", fill = true)
        )
    )

    // Skip back / forward 15 seconds — audio player transport (from Tabler Icons)
    val rewindBackward15 = SVGIcon(
        paths = listOf(
            SVGPath(d = "M8 20h2a1 1 0 0 0 1 -1v-1a1 1 0 0 0 -1 -1h-2v-3h3"),
            SVGPath(d = "M15 18a6 6 0 1 0 0 -12h-11"),
            SVGPath(d = "M5 14v6"),
            SVGPath(d = "M7 9l-3 -3l3 -3")
        )
    )
    val rewindForward15 = SVGIcon(
        paths = listOf(
            SVGPath(d = "M17 9l3 -3l-3 -3"),
            SVGPath(d = "M9 18a6 6 0 1 1 0 -12h11"),
            SVGPath(d = "M16 20h2a1 1 0 0 0 1 -1v-1a1 1 0 0 0 -1 -1h-2v-3h3"),
            SVGPath(d = "M13 14v6")
        )
    )

    // Speaker / volume icon for the audio player volume control (from Tabler Icons - volume-2)
    val volume = SVGIcon(
        paths = listOf(
            SVGPath(d = "M15 8a5 5 0 0 1 0 8"),
            SVGPath(d = "M6 15h-2a1 1 0 0 1 -1 -1v-4a1 1 0 0 1 1 -1h2l3.5 -4.5a.8 .8 0 0 1 1.5 .5v14a.8 .8 0 0 1 -1.5 .5l-3.5 -4.5")
        )
    )

    // Chevron up — collapse affordance for the audio player's clip row (from Tabler Icons)
    val chevronUp = SVGIcon("M6 15l6 -6l6 6")

    // Download icon — for the audio player "Download clip" action (from Tabler Icons)
    val download = SVGIcon(
        paths = listOf(
            SVGPath(d = "M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"),
            SVGPath(d = "M7 11l5 5l5 -5"),
            SVGPath(d = "M12 4l0 12")
        )
    )

    // Brand icons (stroke-based, from Tabler Icons)
    val github = SVGIcon("M9 19c-4.3 1.4 -4.3 -2.5 -6 -3m12 5v-3.5c0 -1 .1 -1.4 -.5 -2c2.8 -.3 5.5 -1.4 5.5 -6a4.6 4.6 0 0 0 -1.3 -3.2a4.2 4.2 0 0 0 -.1 -3.2s-1.1 -.3 -3.5 1.3a12.3 12.3 0 0 0 -6.2 0c-2.4 -1.6 -3.5 -1.3 -3.5 -1.3a4.2 4.2 0 0 0 -.1 3.2a4.6 4.6 0 0 0 -1.3 3.2c0 4.6 2.7 5.7 5.5 6c-.6 .6 -.6 1.2 -.5 2v3.5")

    // Brand icons (fill-based, from SimpleIcons)
    val jira = SVGIcon(
        SVGPath(
            d = "M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.001 1.001 0 0 0 23.013 0Z",
            fill = true
        )
    )

    val datadog = SVGIcon(
        SVGPath(
            d = "M19.57 17.04l-1.997-1.316-1.665 2.782-1.937-.567-1.706 2.604.087.82 9.274-1.71-.538-5.794zm-8.649-2.498l1.488-.204c.241.108.409.15.697.223.45.117.97.23 1.741-.16.18-.088.553-.43.704-.625l6.096-1.106.622 7.527-10.444 1.882zm11.325-2.712l-.602.115L20.488 0 .789 2.285l2.427 19.693 2.306-.334c-.184-.263-.471-.581-.96-.989-.68-.564-.44-1.522-.039-2.127.53-1.022 3.26-2.322 3.106-3.956-.056-.594-.15-1.368-.702-1.898-.02.22.017.432.017.432s-.227-.289-.34-.683c-.112-.15-.2-.199-.319-.4-.085.233-.073.503-.073.503s-.186-.437-.216-.807c-.11.166-.137.48-.137.48s-.241-.69-.186-1.062c-.11-.323-.436-.965-.343-2.424.6.421 1.924.321 2.44-.439.171-.251.288-.939-.086-2.293-.24-.868-.835-2.16-1.066-2.651l-.028.02c.122.395.374 1.223.47 1.625.293 1.218.372 1.642.234 2.204-.116.488-.397.808-1.107 1.165-.71.358-1.653-.514-1.713-.562-.69-.55-1.224-1.447-1.284-1.883-.062-.477.275-.763.445-1.153-.243.07-.514.192-.514.192s.323-.334.722-.624c.165-.109.262-.178.436-.323a9.762 9.762 0 0 0-.456.003s.42-.227.855-.392c-.318-.014-.623-.003-.623-.003s.937-.419 1.678-.727c.509-.208 1.006-.147 1.286.257.367.53.752.817 1.569.996.501-.223.653-.337 1.284-.509.554-.61.99-.688.99-.688s-.216.198-.274.51c.314-.249.66-.455.66-.455s-.134.164-.259.426l.03.043c.366-.22.797-.394.797-.394s-.123.156-.268.358c.277-.002.838.012 1.056.037 1.285.028 1.552-1.374 2.045-1.55.618-.22.894-.353 1.947.68.903.888 1.609 2.477 1.259 2.833-.294.295-.874-.115-1.516-.916a3.466 3.466 0 0 1-.716-1.562 1.533 1.533 0 0 0-.497-.85s.23.51.23.96c0 .246.03 1.165.424 1.68-.039.076-.057.374-.1.43-.458-.554-1.443-.95-1.604-1.067.544.445 1.793 1.468 2.273 2.449.453.927.186 1.777.416 1.997.065.063.976 1.197 1.15 1.767.306.994.019 2.038-.381 2.685l-1.117.174c-.163-.045-.273-.068-.42-.153.08-.143.241-.5.243-.572l-.063-.111c-.348.492-.93.97-1.414 1.245-.633.359-1.363.304-1.838.156-1.348-.415-2.623-1.327-2.93-1.566 0 0-.01.191.048.234.34.383 1.119 1.077 1.872 1.56l-1.605.177.759 5.908c-.337.048-.39.071-.757.124-.325-1.147-.946-1.895-1.624-2.332-.599-.384-1.424-.47-2.214-.314l-.05.059a2.851 2.851 0 0 1 1.863.444c.654.413 1.181 1.481 1.375 2.124.248.822.42 1.7-.248 2.632-.476.662-1.864 1.028-2.986.237.3.481.705.876 1.25.95.809.11 1.577-.03 2.106-.574.452-.464.69-1.434.628-2.456l.714-.104.258 1.834 11.827-1.424zM15.05 6.848c-.034.075-.085.125-.007.37l.004.014.013.032.032.073c.14.287.295.558.552.696.067-.011.136-.019.207-.023.242-.01.395.028.492.08.009-.048.01-.119.005-.222-.018-.364.072-.982-.626-1.308-.264-.122-.634-.084-.757.068a.302.302 0 0 1 .058.013c.186.066.06.13.027.207m1.958 3.392c-.092-.05-.52-.03-.821.005-.574.068-1.193.267-1.328.372-.247.191-.135.523.047.66.511.382.96.638 1.432.575.29-.038.546-.497.728-.914.124-.288.124-.598-.058-.698m-5.077-2.942c.162-.154-.805-.355-1.556.156-.554.378-.571 1.187-.041 1.646.053.046.096.078.137.104a4.77 4.77 0 0 1 1.396-.412c.113-.125.243-.345.21-.745-.044-.542-.455-.456-.146-.749",
            fill = true
        )
    )

    // Slack monochrome icon (fill-based, from SimpleIcons) - inherits currentColor
    val slack = SVGIcon(
        SVGPath(
            d = "M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z",
            fill = true
        )
    )

    // Slack full-color brand icon with official brand colors
    val slackColor = SVGIcon(
        paths = listOf(
            // Aubergine (#E01E5A) - left vertical bar and dot
            SVGPath(d = "M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52z", fill = true, color = "#E01E5A"),
            SVGPath(d = "M6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z", fill = true, color = "#E01E5A"),
            // Blue (#36C5F0) - top horizontal bar and dot
            SVGPath(d = "M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834z", fill = true, color = "#36C5F0"),
            SVGPath(d = "M8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z", fill = true, color = "#36C5F0"),
            // Green (#2EB67D) - right vertical bar and dot
            SVGPath(d = "M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834z", fill = true, color = "#2EB67D"),
            SVGPath(d = "M17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z", fill = true, color = "#2EB67D"),
            // Yellow (#ECB22E) - bottom horizontal bar and dot
            SVGPath(d = "M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52z", fill = true, color = "#ECB22E"),
            SVGPath(d = "M15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z", fill = true, color = "#ECB22E")
        )
    )

    // Star outline - empty star for unstarred items (from Tabler Icons)
    val star = SVGIcon("M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873l-6.158 -3.245")

    // Star filled - solid star for starred items (from Tabler Icons)
    val starFilled = SVGIcon(
        SVGPath(
            d = "M8.243 7.34l-6.38 .925l-.113 .023a1 1 0 0 0 -.44 1.684l4.622 4.499l-1.09 6.355l-.013 .11a1 1 0 0 0 1.464 .944l5.706 -3l5.693 3l.1 .046a1 1 0 0 0 1.352 -1.1l-1.091 -6.355l4.624 -4.5l.078 -.085a1 1 0 0 0 -.633 -1.62l-6.38 -.926l-2.852 -5.78a1 1 0 0 0 -1.794 0l-2.853 5.78z",
            fill = true
        )
    )

    // Arrow right - simple right arrow (from Tabler Icons)
    val arrowRight = SVGIcon(
        paths = listOf(
            SVGPath(d = "M5 12l14 0"),
            SVGPath(d = "M13 18l6 -6"),
            SVGPath(d = "M13 6l6 6")
        )
    )

    // Filter - funnel shape used to reveal filtering controls (from Tabler Icons)
    val filter = SVGIcon("M4 4h16v2.172a2 2 0 0 1 -.586 1.414l-4.414 4.414v7l-6 2v-8.5l-4.48 -4.928a2 2 0 0 1 -.52 -1.345v-2.227")

    // External link - arrow pointing out of a box (from Tabler Icons)
    val externalLink = SVGIcon(
        paths = listOf(
            SVGPath(d = "M12 6h-6a2 2 0 0 0 -2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-6"),
            SVGPath(d = "M11 13l9 -9"),
            SVGPath(d = "M15 4h5v5")
        )
    )

    // Square arrow right - blocky arrow in a square (from Tabler Icons)
    val squareArrowRight = SVGIcon(
        paths = listOf(
            SVGPath(d = "M12 16l4 -4l-4 -4"),
            SVGPath(d = "M8 12h8"),
            SVGPath(d = "M3 5a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v14a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-14z")
        )
    )

    // Settings gear icon (from Tabler Icons)
    val settings = SVGIcon(
        paths = listOf(
            SVGPath(d = "M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065"),
            SVGPath(d = "M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0")
        )
    )

    // Calendar icon (from Tabler Icons)
    val calendar = SVGIcon(
        paths = listOf(
            SVGPath(d = "M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12"),
            SVGPath(d = "M16 3v4"),
            SVGPath(d = "M8 3v4"),
            SVGPath(d = "M4 11h16"),
            SVGPath(d = "M11 15h1"),
            SVGPath(d = "M12 15v3")
        )
    )

    // SiriusXM 'S' with star icon (extracted from company logo)
    val siriusxm = SVGIcon(
        path = SVGPath(
            d = "M20.2035 13.3517C20.6909 14.0985 20.9817 15.0104 20.9817 16.1346C20.9503 20.2853 17.5149 23.0997 10.1253 23.0997C5.50289 23.0997 0.424509 21.6925 0 16.3232V16.056H6.92578L5.39283 20.5762L10.4476 17.0622L15.5024 20.5762L14.1896 16.6927C13.9852 16.2918 13.4585 16.0717 13.4585 16.0717C10.6992 14.5387 0.628902 15.372 0.628902 8.59561C0.628902 3.49364 5.98243 2.00786 10.1803 2.00786C14.5748 2.00786 19.7318 2.99838 20.1956 8.28902V8.49341H12.3972L10.4634 2.80185L8.52948 8.49341H2.27977L5.77017 10.899C6.79214 10.4902 7.88486 10.3094 8.97757 10.1914C10.1489 10.0657 11.3281 10.0421 12.5073 10.1207C13.4978 10.1836 14.4805 10.3251 15.4474 10.5531C16.32 10.7575 17.2083 10.9854 18.0023 11.4178C18.8906 11.9052 19.6689 12.5262 20.2035 13.3517Z",
            fill = true
        ),
        viewBox = "-0.5 1.5 22 22"
    )

    // Info circle icon (from Tabler Icons)
    val infoCircle = SVGIcon(
        paths = listOf(
            SVGPath(d = "M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"),
            SVGPath(d = "M12 9h.01"),
            SVGPath(d = "M11 12h1v4h1")
        )
    )

    // Shield icon - for admin/protected status (from Tabler Icons)
    val shield = SVGIcon("M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3")

    // Trash icon - for delete actions (from Tabler Icons)
    val trash = SVGIcon(
        paths = listOf(
            SVGPath(d = "M4 7l16 0"),
            SVGPath(d = "M10 11l0 6"),
            SVGPath(d = "M14 11l0 6"),
            SVGPath(d = "M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"),
            SVGPath(d = "M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3")
        )
    )

    // Satellite icon - for broadcast/transmission (from Tabler Icons)
    val satellite = SVGIcon(
        paths = listOf(
            SVGPath(d = "M3.707 6.293l2.586 -2.586a1 1 0 0 1 1.414 0l5.586 5.586a1 1 0 0 1 0 1.414l-2.586 2.586a1 1 0 0 1 -1.414 0l-5.586 -5.586a1 1 0 0 1 0 -1.414"),
            SVGPath(d = "M6 10l-3 3l3 3l3 -3"),
            SVGPath(d = "M10 6l3 -3l3 3l-3 3"),
            SVGPath(d = "M12 12l1.5 1.5"),
            SVGPath(d = "M14.5 17a2.5 2.5 0 0 0 2.5 -2.5"),
            SVGPath(d = "M15 21a6 6 0 0 0 6 -6")
        )
    )

    // Pencil icon - for edit actions (from Tabler Icons)
    val pencil = SVGIcon(
        paths = listOf(
            SVGPath(d = "M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4"),
            SVGPath(d = "M13.5 6.5l4 4")
        )
    )

    // Broken/missing image placeholder icon (from Tabler Icons - photo-off)
    val photoOff = SVGIcon(
        paths = listOf(
            SVGPath(d = "M15 8h.01"),
            SVGPath(d = "M7 3h11a3 3 0 0 1 3 3v11m-.856 3.099a2.991 2.991 0 0 1 -2.144 .901h-12a3 3 0 0 1 -3 -3v-12c0 -.845 .349 -1.608 .91 -2.153"),
            SVGPath(d = "M3 16l5 -5c.928 -.893 2.072 -.893 3 0l5 5"),
            SVGPath(d = "M16.33 12.338c.574 -.054 1.155 .166 1.67 .662l3 3"),
            SVGPath(d = "M3 3l18 18")
        )
    )

    // Horizontal ellipsis (three dots) icon - for menus and more options (from Tabler Icons)
    val dots = SVGIcon(
        paths = listOf(
            SVGPath(d = "M4 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"),
            SVGPath(d = "M11 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"),
            SVGPath(d = "M18 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0")
        )
    )

    // Stack forward icon - indicates a field is overridden by a partial (from Tabler Icons - stack-forward)
    val stackForward = SVGIcon(
        paths = listOf(
            SVGPath(d = "M12 5l-8 4l8 4l8 -4l-8 -4", fill = true),
            SVGPath(d = "M10 12l-6 3l8 4l8 -4l-6 -3")
        )
    )

    // User icon - person silhouette for account/profile (from Tabler Icons)
    val user = SVGIcon(
        paths = listOf(
            SVGPath(d = "M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"),
            SVGPath(d = "M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2")
        )
    )

    // Arrow up - upward arrow for send button (from Tabler Icons)
    val arrowUp = SVGIcon(
        paths = listOf(
            SVGPath(d = "M12 5l0 14"),
            SVGPath(d = "M18 11l-6 -6"),
            SVGPath(d = "M6 11l6 -6")
        )
    )

    // Message circle - chat bubble icon (from Tabler Icons)
    val messageCircle = SVGIcon(
        paths = listOf(
            SVGPath(d = "M3 20l1.3 -3.9c-2.324 -3.437 -1.426 -7.872 2.1 -10.374c3.526 -2.501 8.59 -2.296 11.845 .48c3.255 2.777 3.695 7.266 1.029 10.501c-2.666 3.235 -7.615 4.215 -11.574 2.293l-4.7 1")
        )
    )
}
